import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { db, STORES } from '../db/database';

export const DataContext = createContext();

/* ============================================================
   Aides dates : les exports Tolteck stockent des dates en
   "numéro de série Excel". On convertit vers des dates ISO
   (YYYY-MM-DD) pour un calcul correct des mois.
   ============================================================ */
const MS_PER_DAY = 86400000;
const EXCEL_EPOCH_OFFSET_MS = 25569 * MS_PER_DAY; // 1970-01-01 vs 1899-12-30

const excelSerialToDate = (v) => {
  if (v == null || v === '') return null;
  if (typeof v === 'number') {
    return new Date(v * MS_PER_DAY - EXCEL_EPOCH_OFFSET_MS);
  }
  if (v instanceof Date) return v;
  const str = String(v);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return null;
};

const dateToISO = (v) => {
  const d = excelSerialToDate(v);
  return d ? d.toISOString().slice(0, 10) : '';
};

const monthIndexOf = (v) => {
  const d = excelSerialToDate(v);
  return d ? d.getUTCMonth() : -1;
};

export const DataProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [devis, setDevis] = useState([]);
  const [factures, setFactures] = useState([]);
  const [couts, setCouts] = useState([]);
  const [coutsStructure, setCoutsStructure] = useState([]);
  const [regroupements, setRegroupements] = useState([]);
  const [parametres, setParametres] = useState({
    tvaParDefaut: 0.2,
    anneeDashboard: new Date().getFullYear()
  });
  const [kpi, setKpi] = useState({});

  const loadData = async () => {
    try {
      await db.init();
      const [clientsData, devisData, facturesData, coutsData, coutsStructureData, regroupementsData] = await Promise.all([
        db.getAll(STORES.CLIENTS),
        db.getAll(STORES.DEVIS),
        db.getAll(STORES.FACTURES),
        db.getAll(STORES.COUTS),
        db.getAll(STORES.COUTSTRUCT),
        db.getAll(STORES.REGROUPEMENTS),
      ]);

      setClients(clientsData);
      setDevis(devisData);
      setFactures(facturesData);
      setCouts(coutsData);
      setCoutsStructure(coutsStructureData);
      setRegroupements(regroupementsData);
      
      const tva = await db.get(STORES.PARAMETRES, 'tvaParDefaut');
      const annee = await db.get(STORES.PARAMETRES, 'anneeDashboard');
      if (tva) setParametres(prev => ({ ...prev, tvaParDefaut: tva.value }));
      if (annee) setParametres(prev => ({ ...prev, anneeDashboard: annee.value }));
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setLoading(false);
    }
  };

  const calculerKPI = (clientsData, devisData, facturesData, coutsData, coutsStructureData) => {
    const totalClients = clientsData.length;
    const totalDevis = devisData.length;
    const devisAcceptes = devisData.filter(d => d.statut === 'Accepté').length;
    const tauxAcceptation = totalDevis > 0 ? (devisAcceptes / totalDevis * 100) : 0;
    
    const caFactureHT = facturesData.reduce((acc, f) => acc + f.montantHT, 0);
    const totalCoutsHT = coutsData.reduce((acc, c) => acc + c.montantHT, 0);
    const margeBrute = caFactureHT - totalCoutsHT;
    const margeBrutePct = caFactureHT > 0 ? (margeBrute / caFactureHT * 100) : 0;
    
    // --- Coûts de structure (charges générales, hors chantier) ---
    const cs = coutsStructureData || [];
    const coutsStructureHT = cs.reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0), 0);
    const coutsStructureTVA = cs.reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0) * (parseFloat(c.tva) || 0), 0);
    const coutsStructureTTC = coutsStructureHT + coutsStructureTVA;
    const coutsStructurePayesTTC = cs
      .filter(c => c.paye)
      .reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0) * (1 + (parseFloat(c.tva) || 0)), 0);

    // Résultat net = marge brute (CA − coûts directs) − charges de structure
    const resultatNet = margeBrute - coutsStructureHT;
    const resultatNetPct = caFactureHT > 0 ? (resultatNet / caFactureHT * 100) : 0;

    const tvaCollectee = facturesData.reduce((acc, f) => acc + (f.tva || 0), 0);
    // TVA déductible = achats directs (Tolteck) + charges de structure
    const tvaDeductible = coutsData.reduce((acc, c) => acc + (c.montantHT * c.tva || 0), 0) + coutsStructureTVA;
    const tvaNette = tvaCollectee - tvaDeductible;
    
    const encaisseTTC = facturesData.filter(f => f.statut === 'Payée').reduce((acc, f) => acc + f.montantTTC, 0);
    const totalTTC = facturesData.reduce((acc, f) => acc + f.montantTTC, 0);
    const resteAEncaisser = totalTTC - encaisseTTC;
    
    // Top 10 chantiers
    const chantiersCA = facturesData.reduce((acc, f) => {
      if (!acc[f.chantier]) acc[f.chantier] = { ca: 0, couts: 0 };
      acc[f.chantier].ca += f.montantHT;
      return acc;
    }, {});
    
    coutsData.forEach(c => {
      if (chantiersCA[c.chantier]) {
        chantiersCA[c.chantier].couts += c.montantHT;
      }
    });
    
    const topChantiers = Object.entries(chantiersCA)
      .map(([chantier, data]) => ({ 
        chantier, 
        ca: data.ca,
        couts: data.couts || 0,
        marge: data.ca - (data.couts || 0)
      }))
      .sort((a, b) => b.ca - a.ca)
      .slice(0, 10);
    
    // Top 10 clients
    const clientsCA = facturesData.reduce((acc, f) => {
      if (!acc[f.client]) acc[f.client] = { ca: 0, couts: 0 };
      acc[f.client].ca += f.montantHT;
      return acc;
    }, {});
    
    coutsData.forEach(c => {
      if (clientsCA[c.client]) {
        clientsCA[c.client].couts += c.montantHT;
      }
    });
    
    const topClients = Object.entries(clientsCA)
      .map(([client, data]) => ({ 
        client, 
        ca: data.ca,
        couts: data.couts || 0,
        marge: data.ca - (data.couts || 0)
      }))
      .sort((a, b) => b.ca - a.ca)
      .slice(0, 10);
    
    // Devis par statut
    const statuts = ['En attente', 'Accepté', 'Refusé'];
    const devisParStatut = statuts.map(statut => ({
      statut,
      nb: devisData.filter(d => d.statut === statut).length,
      montantHT: devisData.filter(d => d.statut === statut).reduce((acc, d) => acc + d.montantHT, 0)
    }));
    
    // CA et coûts par mois
    const mois = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    const caParMois = mois.map((nom, index) => {
      const total = facturesData
        .filter(f => monthIndexOf(f.date) === index)
        .reduce((acc, f) => acc + f.montantHT, 0);
      return { mois: nom, ca: total };
    });
    const coutsParMois = mois.map((nom, index) => {
      const total = coutsData
        .filter(c => monthIndexOf(c.date) === index)
        .reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0), 0);
      return { mois: nom, couts: total };
    });

    // TVA collectée (factures) vs déductible (achats) par mois
    const tvaParMois = mois.map((nom, index) => {
      const collectee = facturesData
        .filter(f => monthIndexOf(f.date) === index)
        .reduce((acc, f) => acc + (parseFloat(f.tva) || 0), 0);
      const deductible = coutsData
        .filter(c => monthIndexOf(c.date) === index)
        .reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0) * (parseFloat(c.tva) || 0), 0);
      const deductibleStructure = cs
        .filter(c => monthIndexOf(c.date) === index)
        .reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0) * (parseFloat(c.tva) || 0), 0);
      return { mois: nom, collectee, deductible: deductible + deductibleStructure };
    });

    // Coûts fournisseurs — total / payé / à payer (TTC)
    const coutsTotalTTC = coutsData.reduce((acc, c) => {
      const ht = parseFloat(c.montantHT) || 0;
      const tva = parseFloat(c.tva) || 0;
      return acc + ht + ht * tva;
    }, 0);
    const coutsPayesTTC = coutsData
      .filter(c => c.paye)
      .reduce((acc, c) => {
        const ht = parseFloat(c.montantHT) || 0;
        const tva = parseFloat(c.tva) || 0;
        return acc + ht + ht * tva;
      }, 0);
    const coutsAPayerTTC = coutsTotalTTC - coutsPayesTTC;

    // Marge par chantier
    const margeParChantier = facturesData.reduce((acc, f) => {
      if (!acc[f.chantier]) {
        acc[f.chantier] = {
          chantier: f.chantier,
          client: f.client,
          caFactureHT: 0,
          coutsHT: 0,
          tvaCollectee: 0,
          encaisseTTC: 0
        };
      }
      acc[f.chantier].caFactureHT += f.montantHT;
      acc[f.chantier].tvaCollectee += (f.tva || 0);
      if (f.statut === 'Payée') {
        acc[f.chantier].encaisseTTC += f.montantTTC;
      }
      return acc;
    }, {});
    
    coutsData.forEach(c => {
      if (margeParChantier[c.chantier]) {
        margeParChantier[c.chantier].coutsHT += c.montantHT;
      }
    });
    
    Object.values(margeParChantier).forEach(item => {
      item.margeBrute = item.caFactureHT - item.coutsHT;
      item.margeBrutePct = item.caFactureHT > 0 ? (item.margeBrute / item.caFactureHT * 100) : 0;
      item.resteAEncaisser = (item.caFactureHT + item.tvaCollectee) - item.encaisseTTC;
    });
    
    return {
      totalClients,
      totalDevis,
      devisAcceptes,
      tauxAcceptation,
      caFactureHT,
      totalCoutsHT,
      margeBrute,
      margeBrutePct,
      coutsStructureHT,
      coutsStructureTVA,
      coutsStructureTTC,
      coutsStructurePayesTTC,
      coutsStructureAPayerTTC: coutsStructureTTC - coutsStructurePayesTTC,
      resultatNet,
      resultatNetPct,
      tvaCollectee,
      tvaDeductible,
      tvaNette,
      encaisseTTC,
      resteAEncaisser,
      topChantiers,
      topClients,
      devisParStatut,
      caParMois,
      coutsParMois,
      tvaParMois,
      coutsTotalTTC,
      coutsPayesTTC,
      coutsAPayerTTC,
      margeParChantier: Object.values(margeParChantier)
    };
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      const newKpi = calculerKPI(clients, devis, factures, couts, coutsStructure);
      setKpi(newKpi);
    }
  }, [clients, devis, factures, couts, coutsStructure, loading]);

  const importTolteckData = async (data) => {
    try {
      await Promise.all([
        db.clear(STORES.CLIENTS),
        db.clear(STORES.DEVIS),
        db.clear(STORES.FACTURES),
      ]);
      
      const clientsSet = new Set();
      const devisData = [];
      const facturesData = [];
      
      const getClientRegroupe = (nom) => {
        const regrouping = regroupements.find(r => r.type === 'client' && r.nomOriginal === nom);
        return regrouping ? regrouping.nomRegroupe : nom;
      };

      const getChantierRegroupe = (nom) => {
        const regrouping = regroupements.find(r => r.type === 'chantier' && r.nomOriginal === nom);
        return regrouping ? regrouping.nomRegroupe : nom;
      };

      const traduireStatut = (statut) => {
        if (statut === 'Accepté') return 'Accepté';
        if (statut === 'Abandonné') return 'Refusé';
        return 'En attente';
      };

      data.forEach(row => {
        if (!row['Numéro de document']) return;
        
        const client = row['Nom du client'];
        const chantier = row['Nom du chantier'];
        const type = row['Type'] || '';
        
        if (client) clientsSet.add(client);
        
        const clientRegroupe = getClientRegroupe(client);
        const chantierRegroupe = getChantierRegroupe(chantier);
        
        if (type.includes('Devis')) {
          devisData.push({
            numero: row['Numéro de document'],
            date: dateToISO(row['Date']),
            client: clientRegroupe,
            chantier: chantierRegroupe,
            montantHT: parseFloat(row['Montant HT']) || 0,
            tva: parseFloat(row['TVA 20 %']) || 0,
            montantTTC: parseFloat(row['Montant TTC']) || 0,
            statut: traduireStatut(row['Statut']),
            statutOriginal: row['Statut'],
          });
        } else if (type.includes('Facture')) {
          facturesData.push({
            numero: row['Numéro de document'] || '',
            date: dateToISO(row['Date']),
            client: clientRegroupe,
            chantier: chantierRegroupe,
            montantHT: parseFloat(row['Montant HT']) || 0,
            tva: parseFloat(row['TVA 20 %']) || 0,
            montantTTC: parseFloat(row['Montant TTC']) || 0,
            statut: row['Statut'] === 'Payée' ? 'Payée' : 'Non payée',
            statutOriginal: row['Statut'],
          });
        }
      });
      
      const clientsData = Array.from(clientsSet).map(nom => ({
        nom,
        adresse: data.find(r => r['Nom du client'] === nom)?.['Adresse de facturation'] || '',
        nbDevis: devisData.filter(d => d.client === nom).length,
        nbFactures: facturesData.filter(f => f.client === nom).length,
      }));
      
      await Promise.all(clientsData.map(c => db.add(STORES.CLIENTS, c)));
      await Promise.all(devisData.map(d => db.add(STORES.DEVIS, d)));
      await Promise.all(facturesData.map(f => db.add(STORES.FACTURES, f)));
      
      await loadData();
      
      return { success: true, message: `${devisData.length} devis et ${facturesData.length} factures importés` };
    } catch (error) {
      console.error('Erreur import:', error);
      return { success: false, message: error.message };
    }
  };

  const refreshData = async () => {
    await loadData();
  };

  // Liste unique des chantiers (issues des devis, factures et coûts)
  // avec le client associé — sert à la saisie manuelle des coûts.
  const chantiers = useMemo(() => {
    const map = {};
    const add = (chantier, client) => {
      if (!chantier) return;
      if (!map[chantier]) map[chantier] = { chantier, client: '' };
      if (client) map[chantier].client = client;
    };
    devis.forEach(d => add(d.chantier, d.client));
    factures.forEach(f => add(f.chantier, f.client));
    couts.forEach(c => add(c.chantier, c.client));
    return Object.values(map).sort((a, b) => a.chantier.localeCompare(b.chantier, 'fr'));
  }, [devis, factures, couts]);

  const value = {
    loading,
    clients,
    devis,
    factures,
    couts,
    coutsStructure,
    regroupements,
    parametres,
    kpi,
    chantiers,
    importTolteckData,
    setParametres,
    setCouts,
    setRegroupements,
    refreshData,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);